import { ApolloProvider } from '@apollo/client';
import { BrowserRouter, Navigate, Outlet, Route, Routes } from 'react-router-dom';
import apolloClient from './apolloClient';
import { AuthProvider, useAuth } from './auth/AuthContext';
import Login from './auth/Login';
import AdminLayout from './components/AdminLayout';
import { DateRangeProvider } from './modules/home/DateRangeContext';
import HomeModule from './modules/home/HomeModule';
import ProductModule from './modules/product/ProductModule';
import ExperimentsModule, {
  ExperimentCreate,
  ExperimentDetail,
  ExperimentsIndex,
} from './modules/experiments/ExperimentsModule';
import GrowthModule from './modules/growth/GrowthModule';
import VideosModule from './modules/growth/VideosModule';
import RevenueModule from './modules/revenue/RevenueModule';
import DecisionsModule from './modules/decisions/DecisionsModule';
import UsersModule from './modules/users/UsersModule';
import OperationsModule from './modules/operations/OperationsModule';
import EconomicsModule from './modules/economics/EconomicsModule';
import ForecastModule from './modules/forecast/ForecastModule';
import { allowAdminWithoutMfa } from './auth/localBreakGlass';
import './App.css';

function AppRoutes() {
  const { loadingUser, user, isAdmin, isStaff } = useAuth();

  const protectedRoute = loadingUser ? (
    <div className="route-loading" role="status">
      Loading session…
    </div>
  ) : !user ? (
    <Navigate to="/login" replace />
  ) : !isAdmin && !isStaff ? (
    <Navigate to="/login?reason=access_denied" replace />
  ) : isAdmin && !user.mfaEnabled && !allowAdminWithoutMfa() ? (
    <Navigate to="/login?reason=mfa_required" replace />
  ) : (
    <Outlet />
  );

  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={protectedRoute}>
        <Route element={<AdminLayout />}>
          <Route
            element={
              <DateRangeProvider>
                <Outlet />
              </DateRangeProvider>
            }
          >
            <Route index element={<HomeModule />} />
            <Route path="growth" element={<GrowthModule />} />
            <Route path="growth/videos" element={<VideosModule />} />
            <Route path="revenue" element={<RevenueModule />} />
            <Route path="product" element={<ProductModule />} />
            <Route path="experiments" element={<ExperimentsModule />}>
              <Route index element={<ExperimentsIndex />} />
              <Route path="new" element={<ExperimentCreate />} />
              <Route path=":key" element={<ExperimentDetail />} />
            </Route>
          </Route>
          <Route path="decisions" element={<DecisionsModule />} />
          <Route path="economics" element={<EconomicsModule />} />
          <Route path="forecast" element={<ForecastModule />} />
          <Route path="users" element={<UsersModule />} />
          <Route path="operations" element={<OperationsModule />} />
        </Route>
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ApolloProvider client={apolloClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ApolloProvider>
  );
}
